import { Edge, Node } from 'reactflow';

export interface LoopInfo {
  isLoop: boolean;
  loopEdges: string[];
  loopStartNode?: string;
  loopEndNode?: string;
}

export const detectLoops = (nodes: Node[], edges: Edge[]): LoopInfo => {
  const loopEdges: string[] = [];
  const processedNodes = new Set<string>();
  const allDetectedLoops: Array<{ nodeId: string, hasConditionNode: boolean, loopNodes: string[], loopEdges: string[] }> = [];

  for (const node of nodes) {
    if (processedNodes.has(node.id)) continue;

    const result = analyzePath(node.id, edges, 'flow', nodes);

    if (result.hasLoop) {
      allDetectedLoops.push({
        nodeId: node.id,
        hasConditionNode: result.hasConditionNode,
        loopNodes: result.loopNodes,
        loopEdges: result.loopEdges
      });
    }
  }

  for (const loopInfo of allDetectedLoops) {
    if (loopInfo.hasConditionNode) {
      const loopNodeIds = new Set(loopInfo.loopNodes);
      const allLoopEdges = edges.filter(edge =>
        loopNodeIds.has(edge.source) && loopNodeIds.has(edge.target)
      );

      for (const edge of allLoopEdges) {
        if (!loopEdges.includes(edge.id)) {
          loopEdges.push(edge.id);
        }
      }

      for (const loopNode of loopInfo.loopNodes) {
        processedNodes.add(loopNode);
      }
    }
  }

  return {
    isLoop: loopEdges.length > 0,
    loopEdges: [...new Set(loopEdges)],
  };
};

const analyzePath = (
  startNodeId: string,
  edges: Edge[],
  handleType: 'true' | 'false' | 'flow',
  allNodes: Node[]
): { hasLoop: boolean; hasConditionNode: boolean; loopEdges: string[]; loopNodes: string[]; path: string[] } => {
  const path: string[] = [];
  const loopEdges: string[] = [];
  const loopNodes: string[] = [];
  let hasLoop = false;
  let hasConditionNode = false;

  const traverse = (nodeId: string, currentPath: string[] = [], currentPathEdges: string[] = []): boolean => {
    if (currentPath.includes(nodeId)) {
      hasLoop = true;
      const loopStartIndex = currentPath.indexOf(nodeId);

      if (loopStartIndex !== -1) {
        const actualLoopNodes = currentPath.slice(loopStartIndex);
        const actualLoopEdges = currentPathEdges.slice(loopStartIndex);

        const loopNodesWithTypes = actualLoopNodes.map(nodeId => {
          const node = allNodes.find(n => n.id === nodeId);
          return { id: nodeId, type: node?.type };
        });

        const hasConditionNodeInLoop = loopNodesWithTypes.some(node => {
          const isConditionNode = node.type === 'conditionNode';
          return isConditionNode;
        });

        if (hasConditionNodeInLoop) {
          hasConditionNode = true;

          for (const node of actualLoopNodes) {
            if (!loopNodes.includes(node)) {
              loopNodes.push(node);
            }
          }

          for (const edgeId of actualLoopEdges) {
            if (!loopEdges.includes(edgeId)) {
              loopEdges.push(edgeId);
            }
          }

          const lastEdge = edges.find(e =>
            e.source === actualLoopNodes[actualLoopNodes.length - 1] &&
            e.target === actualLoopNodes[0]
          );
          if (lastEdge && !loopEdges.includes(lastEdge.id)) {
            loopEdges.push(lastEdge.id);
          }
        } else {
          hasLoop = false;
        }
      }
      return true;
    }

    path.push(nodeId);
    currentPath.push(nodeId);

    const nextEdges = edges.filter(edge => {
      if (edge.source !== nodeId) return false;

      if (handleType === 'true') {
        return edge.sourceHandle === 'outputHandle-0' || edge.sourceHandle === 'true';
      } else if (handleType === 'false') {
        return edge.sourceHandle === 'outputHandle-1' || edge.sourceHandle === 'false';
      } else {
        return edge.sourceHandle === 'outputFlow' ||
          edge.sourceHandle === 'output' ||
          edge.sourceHandle === 'outputHandle-0' ||
          edge.sourceHandle === 'outputHandle-1' ||
          edge.sourceHandle === 'true' ||
          edge.sourceHandle === 'false' ||
          edge.sourceHandle === 'a';
      }
    });

    for (const edge of nextEdges) {
      const newPathEdges = [...currentPathEdges, edge.id];
      if (traverse(edge.target, [...currentPath], newPathEdges)) {
        return true;
      }
    }

    return false;
  };

  traverse(startNodeId);

  return {
    hasLoop,
    hasConditionNode,
    loopEdges,
    loopNodes,
    path,
  };
};

export const isEdgeInLoop = (edgeId: string, loopInfo: LoopInfo): boolean => {
  return loopInfo.loopEdges.includes(edgeId);
};

export const getDecisionPathLoopInfo = (
  decisionNodeId: string,
  nodes: Node[],
  edges: Edge[]
): LoopInfo => {
  const decisionNode = nodes.find(node => node.id === decisionNodeId);
  if (!decisionNode || decisionNode.type !== 'conditionNode') {
    return { isLoop: false, loopEdges: [] };
  }

  const truePathResult = analyzePath(decisionNodeId, edges, 'true', nodes);

  return {
    isLoop: truePathResult.hasLoop && truePathResult.hasConditionNode,
    loopEdges: truePathResult.loopEdges,
    loopStartNode: truePathResult.hasLoop ? decisionNodeId : undefined,
  };
}; 